import type { CambiarPasswordInput, LoginInput } from "@copi/shared";

import { apiClient } from "@/lib/api/client";
import { unwrap } from "@/lib/api/unwrap";

interface LoginResponse {
  accessToken: string;
}

interface UsuarioMe {
  id: string;
  correo: string;
  nombres: string;
  apellidos: string;
  rol: string;
  estado: string;
  /** Capacidades del docente (ADR 054): habilitan navegación/acciones por capacidad. */
  puedeAsesorar: boolean;
  puedeSerJurado: boolean;
}

/**
 * Refresh en vuelo compartido (single-flight). El refresh token ROTA en el
 * backend (revoca el viejo + emite uno nuevo en la misma tx), así que dos
 * llamadas concurrentes mandan la MISMA cookie: la primera la revoca y la
 * segunda choca contra un token ya revocado → 401 espurio. Pasa en el arranque
 * (React StrictMode dispara el effect dos veces en dev) y en prod ante una
 * ráfaga de 401 o varias pestañas. Coalescer en una sola request garantiza una
 * única rotación y que todos los llamantes reciban el mismo access token nuevo.
 */
let refreshEnVuelo: Promise<LoginResponse> | null = null;

export const authApi = {
  login: (dto: LoginInput): Promise<LoginResponse> =>
    apiClient.post<{ data: LoginResponse }>("/auth/login", dto).then(unwrap),

  refresh: (): Promise<LoginResponse> => {
    if (refreshEnVuelo) return refreshEnVuelo;
    refreshEnVuelo = apiClient
      .post<{ data: LoginResponse }>("/auth/refresh")
      .then(unwrap)
      .finally(() => {
        refreshEnVuelo = null;
      });
    return refreshEnVuelo;
  },

  logout: (): Promise<void> => apiClient.post("/auth/logout").then(() => undefined),

  me: (): Promise<UsuarioMe> => apiClient.get<{ data: UsuarioMe }>("/usuarios/me").then(unwrap),

  /**
   * Cambio de contraseña del usuario autenticado (Fase 5 §F5-013).
   * Requiere conocer la contraseña actual.
   */
  cambiarPassword: (dto: CambiarPasswordInput): Promise<void> =>
    apiClient.post("/auth/cambiar-password", dto).then(() => undefined),
};
